import apiV2 from "api-v2";
import type { ResolvedDesignSchema as DesignSchema } from "newui/features/Settings/PdfSettings/types/DesignSchema.type";
import type { ReportPreview } from "../types/report.type";
import { unwrap } from "newui/utils/apiResponse";

/**
 * Final-report type on the wire.
 *
 * NOTE (T011/T007 contract): the brief's API contract uses `'awarded' | 'all'`;
 * this replaces the placeholder `'awarded_supplier' | 'all_supplier_scoring'`
 * literal from the TASK-005 stub so FE and backend agree. Flagged to the lead.
 */
export type ReportType = "awarded" | "all";

export interface ReportPreviewParams {
	type: ReportType;
	/** Full design schema for live brand preview. Optional. */
	design_schema?: DesignSchema;
	/** Required when `type === 'awarded'` — which awarded supplier to render. */
	invite_id?: number;
}

export interface ReportExportPayload {
	type: ReportType;
	design_schema?: DesignSchema;
	invite_id?: number;
}

export interface ReportExportResponse {
	download_url: string;
	filename: string;
}

const reportBase = (rfxId: number, evalId: number): string =>
	`/rfx/${rfxId}/evaluation/${evalId}/final-report`;

/**
 * `POST /rfx/{rfxId}/evaluation/{evalId}/final-report/preview`
 *
 * Lead ruling (canonical, enforced on TASK-011): the preview is a POST with
 * `type`, `design_schema?` and `invite_id?` in the JSON body — following the
 * established `fetchRfxResponsePreviewHtml` convention, NOT the brief's literal
 * GET (a full DesignSchema does not belong in a querystring).
 */
export const getReportPreview = async (
	rfxId: number,
	evalId: number,
	params: ReportPreviewParams
): Promise<ReportPreview> => {
	const response = await apiV2.post(`${reportBase(rfxId, evalId)}/preview`, params);
	return unwrap<ReportPreview>(response.data);
};

/**
 * `POST /rfx/{rfxId}/evaluation/{evalId}/final-report/export`
 * Returns a server-generated `download_url` — never construct it client-side.
 */
export const exportReport = async (
	rfxId: number,
	evalId: number,
	payload: ReportExportPayload
): Promise<ReportExportResponse> => {
	const response = await apiV2.post(`${reportBase(rfxId, evalId)}/export`, payload);
	return unwrap<ReportExportResponse>(response.data);
};
